import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';

import { pendingJobStates } from '~pages/Dispatch/constants/jobStates';
import useDriverSchedulerFetch from '~pages/Dispatch/hooks/useDriverSchedulerFetch';
import { UnassignedOrderGroup } from '~store/DriverSchedulerStore';
import { useStores } from '~store/RootStore';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import InfiniteList from './InfiniteList';
import { OrderCard } from './OrderCard';

const ORDER_CARD_HEIGHT = 84;

export const UnassignedOrders = observer(() => {
  const theme = useTheme();
  const { fetchAllUnassignedJobs } = useDriverSchedulerFetch();
  const { driverSchedulerStore, userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);

  useEffect(() => {
    // Take ownership of the unassigned-jobs load while this view is mounted so
    // DriversViewEffects defers to the eager loader below.
    driverSchedulerStore.setOrdersViewActive(true);

    return () => {
      driverSchedulerStore.setOrdersViewActive(false);
      // Leaving the sub-tab must stop the eager pager too, or it keeps appending
      // to the list the Jobs view is about to take over.
      driverSchedulerStore.abandonEagerUnassignedLoad();
    };
  }, []);

  useEffect(() => {
    // SharedDatesFilter writes the date after the first render, so fetching
    // before it lands would pull the whole unassigned set for no date range and
    // then immediately refetch. DriversViewEffects gates its fetch the same way.
    if (!driverSchedulerStore.dateFilters.startDate) return;

    void Promise.resolve(
      fetchAllUnassignedJobs({
        states: pendingJobStates,
        companyId,
      }),
    ).catch(() => {
      // Error is handled by the hook.
    });
  }, [
    companyId,
    driverSchedulerStore.dateFilters.startDate,
    driverSchedulerStore.dateFilters.endDate,
    driverSchedulerStore.unassignedJobsFilters,
  ]);

  const groups = driverSchedulerStore.unassignedOrderGroups;
  // Hold the spinner for the whole eager load, not just the first page: an
  // order's jobs can span pages, so cards revealed mid-load show a partial job
  // count and a provisional earliest job.
  const isLoading = driverSchedulerStore.isEagerLoadingUnassignedJobs;
  const isEmpty =
    !isLoading && groups.length === 0 && driverSchedulerStore.isUnassignedJobsFullyLoaded;

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1.5,
          py: 4,
          color: theme.brandV2.colors.treadGray3,
        }}
      >
        <CircularProgress size={24} />
        <Typography sx={{ fontSize: 12, color: theme.brandV2.colors.treadGray3 }}>
          {t('dispatch.drivers.unassigned_loading_orders')}
        </Typography>
      </Box>
    );
  }

  if (isEmpty) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          py: 4,
          px: 2,
          textAlign: 'center',
        }}
      >
        <Typography sx={{ fontSize: 12, color: theme.brandV2.colors.treadGray3 }}>
          {t('dispatch.drivers.unassigned_empty_orders')}
        </Typography>
      </Box>
    );
  }

  return (
    <InfiniteList<UnassignedOrderGroup>
      items={groups}
      itemCount={groups.length}
      itemSize={ORDER_CARD_HEIGHT}
      loadMoreItems={() => {}}
      hasNextPage={false}
      style={{ overflowX: 'hidden' }}
      renderItem={(index, style) => {
        const group = groups[index];
        if (!group) return null;
        return <OrderCard key={group.order.id} orderId={group.order.id} style={style} />;
      }}
    />
  );
});
